import $ from 'jquery';
import _ from 'underscore';
import * as topojson from 'topojson';
import { $body, $html, $window, jqGetElemById, isMobile, setScrollPosition } from 'util/global';
import flags from 'util/flags';
import { track } from 'util/analytics';
import { CLASS_NAME_ACTIVE, CLASS_NAME_HIDE, INTERACTION_TYPES } from 'util/constants';
import { executeFunctionByName } from 'app/generic_controllers';

const nux = {};

/** @type {JQuery} */
let $slideContainer = null;

/** slide number of current visible slide */
let currentSlide = 0;
let firstSlide;
let lastSlide;
let axContext;

/** class prefix for slide, e.g. `nx-slide-1` */
const slidePrefix = 'nx-slide-';

/** the class attached to modal targets */
const modalTargetClass = 'nx-target-active';


/**
 * Reposition the modal
 * @param {JQuery} $modal
 * @param {JQuery} $target
 * @param {boolean} [isHidden]
 */
function repositionModal($modal, $target, isHidden) {
    const ALLOWED_SIDES = ['top', 'bottom'];
    const reposition = $modal.data('reposition');
    if (reposition) {
        _.defer(() => {
            const sides = reposition.split('-');
            const targetSide = sides[0];
            const modalSide = sides[1];
            if (ALLOWED_SIDES.indexOf(targetSide) < 0 || ALLOWED_SIDES.indexOf(modalSide) < 0) {
                return;
            }
            const top = $target.offset().top
                      + (targetSide === 'bottom' ? $target.outerHeight() : 0)
                      - (modalSide === 'bottom' ? $modal.outerHeight() : 0);
            const offset = $modal.offset().top - $modal.position().top;
            $modal.css({
                top: top - offset + parseInt($modal.css('margin-top'), 10),
            });

            // currently hidden so show
            if (isHidden) {
                $modal.show();
            }

            // scroll to the chapters if there's a warmup section
            if (jqGetElemById('prac-topic-header').length) {
                const position = $target.offset().top - jqGetElemById('header').outerHeight() - 28;
                setScrollPosition(position, {
                    animate: true,
                });
            }
        });
    }
}


/**
 * Handle nx-modal elements
 * @param {JQuery} $modal
 */
function nuxModalEvents($modal) {
    // get the active target
    const targetQuery = $modal.data('target') || null;
    if (targetQuery) {
        const $target = $(targetQuery);
        _.defer(() => {
            $target.addClass(modalTargetClass);
            repositionModal($modal, $target);
        });
    }
}


/**
 * Called when the slide is changed
 * @param {JQuery} $slide
 */
function changeSlideDefaultCallback($slide) {
    _.defer(() => {
        $(`.${modalTargetClass}`).off('click.lock').removeClass(modalTargetClass);
        if ($slide.length > 0) {
            $slide.find('.nx-modal').each((i, elem) => {
                const $elem = $(elem);
                const repositionHandler = $slide.data('reposition-handler');
                if (repositionHandler) {
                    executeFunctionByName(repositionHandler, $elem);
                } else {
                    nuxModalEvents($elem);
                }
            });
            _.defer(() => {
                $slide.addClass('in');
            });
        } else {
            $body.trigger('nuxClose');
            $slideContainer.hide();
        }
    });
}


/**
 * Changes the current slide to the provided slide number
 * @param {number} slide
 * @param {string} [triggeredBy]
 */
const setCurrentSlide = (slide, triggeredBy) => {
    const oldSlide = currentSlide;
    currentSlide = slide;
    _.defer(() => {
        // update the container
        $slideContainer
            .removeClass(slidePrefix + oldSlide)
            .addClass(slidePrefix + currentSlide);

        const $slide = $slideContainer.find(`.slide-${currentSlide}`);
        const slideCallback = $slide.data('on-visible') || null;

        changeSlideDefaultCallback($slide);

        // try to call the on-visible method for the new slide
        if (slideCallback) {
            executeFunctionByName(slideCallback, $slide);
        }

        $slideContainer.trigger($.Event('nux.slidechange'));

        if ($slide.length > 0) {
            const eventParams = {
                action: `viewed_${axContext}_popup`,
                step_position: currentSlide,
                is_first_step: currentSlide === firstSlide,
                is_last_step: currentSlide === lastSlide,
            };
            if (triggeredBy) {
                eventParams.triggered_by = triggeredBy;
            }
            track('ui', eventParams);
        }
    });
};


/**
 * Main tour controller
 * @param {JQuery} $container
 */
nux.tour = ($container) => {
    $slideContainer = $container;

    const $slides = $slideContainer.find('.slide');
    firstSlide = parseInt($slides.first().prop('class').match(/slide-(\d+)/)[1], 10);
    lastSlide = firstSlide + $slides.length - 1;
    axContext = $container.data('axContext');

    // determine current slide -- in production, it will probably always be 1, but doing it
    // this way makes debugging easier
    const slideMatches = $slideContainer.prop('class').match(/nx-slide-(\d+)/)[1];
    currentSlide = parseInt(slideMatches, 10);
    setCurrentSlide(currentSlide);

    // clicking next triggers the next slide
    $slideContainer.on('click', '.nx-next', () => {
        setCurrentSlide(currentSlide + 1, INTERACTION_TYPES.CONTINUE_BUTTON);
    });

    _.defer(() => {
        // remove tour=true GET param from the current URL
        if (window.history.replaceState) {
            window.history.replaceState({}, '', window.location.href.replace(/tour=true&?/, '').replace(/[&?]$/, ''));
        }
    });
};


/**
 * Controller for overlays on home and new/popular pages
 * @param {JQuery} $overlay
 */
nux.overlay = ($overlay) => {
    $overlay.on('click', () => {
        flags.update($overlay.data('flag'), true);
        _.defer(() => {
            $overlay.hide();
        });
    });
};


/**
 * Mark the banner flag when the user sees it
 * @param {JQuery} $banner
 */
nux.banner = ($banner) => {
    flags.update($banner.data('flag'), true);
};


nux.potdHighlight = ($slide) => {
    const $modal = $slide.find('.nx-modal');

    setTimeout(() => {
        const offset = $modal.offset();
        const windowHeight = $window.height();
        if (offset.top > windowHeight) {
            const $scrollTarget = $html.add($body);
            const smallPadding = 8; // @smallPadding

            setScrollPosition(offset.top - windowHeight + $modal.height() + smallPadding, {
                $container: $scrollTarget,
                animate: true,
            });

            $slideContainer.on('nux.slidechange', () => {
                setScrollPosition(0, {
                    $container: $scrollTarget,
                    animate: true,
                });
            });
        }
    }, 500);
};


/**
 * Events for the NUX welcome page
 * @param {JQuery} $container
 */
nux.welcomePage = ($container) => {
    const { enableNuxSegmentation, surveyStep, isLastSurveyStep } = $container.data();
    const $slides = $container.find('.nux-welcome-slide');
    const $categoryLinks = $container.find('.category-link');
    const $categoryItems = $container.find('.category-item');
    const slideCount = $container.find('.nux-welcome-slide').length;
    const exampleURL = $container.find('.continue:first').attr('href');
    const getSlideNumberFromURL = () => {
        if (window.location.search.indexOf('step=') >= 0) {
            return parseInt(window.location.search.match(/[?&]step=(\d)/)[1], 10);
        }
        return 1;
    };
    let slide = getSlideNumberFromURL();
    let toggleFooter = _.noop;

    /** @param {string} [triggeredBy] */
    const trackStep = (triggeredBy) => {
        const isLastSlide = slide === slideCount;
        const isSurveySlide = isLastSlide && surveyStep >= 0;
        const eventParams = {
            action: 'viewed_nux_slide',
            is_first_step: slide === 1,
        };
        if (enableNuxSegmentation) {
            // The entire survey is implemented as the last slide and uses 1-based step indexes.
            // We want to track each step of the survey as a separate NUX step.
            eventParams.step_position = isSurveySlide ? slide + surveyStep - 1 : slide;
            eventParams.is_last_step = isSurveySlide ? isLastSurveyStep : isLastSlide;
        } else {
            eventParams.step_position = slide;
            eventParams.is_last_step = isLastSlide;
        }
        if (triggeredBy) {
            eventParams.triggered_by = triggeredBy;
        }
        track('ui', eventParams);
    };

    const switchSlide = (i, url, triggeredBy) => {
        $slides.removeClass(CLASS_NAME_ACTIVE);
        $slides.filter(`.nux-welcome-slide-${i}`).addClass(CLASS_NAME_ACTIVE);
        if (triggeredBy !== INTERACTION_TYPES.PREV_NEXT_BUTTON) {
            try {
                window.history.pushState({}, null, url);
            } catch (err) {
                // do nothing
            }
        }
        trackStep(triggeredBy);
        toggleFooter(i);
    };

    $container.find('.btn-back').on('click', (e) => {
        const $prevCategoryField = $container.find('.prev-category');
        if (isMobile) {
            const { categoryId } = $(e.currentTarget).data();
            $prevCategoryField.val(categoryId);
        }
        $prevCategoryField.prop('checked', true);
    });

    $container.find('.continue').on('click', (e) => {
        e.preventDefault();
        slide++;
        const url = e.currentTarget.href.replace(`step=${slide - 1}`, `step=${slide}`);
        switchSlide(slide, url, INTERACTION_TYPES.CONTINUE_BUTTON);
    });

    $slides.each((stepIndex, stepElem) => {
        $(stepElem).find('.markers span').each((markerIndex, markerElem) => {
            if (markerIndex < stepIndex) {
                $(markerElem).addClass('clickable').on('click', () => {
                    slide = markerIndex + 1;
                    const url = exampleURL.replace(/step=\d/, `step=${slide}`);
                    switchSlide(slide, url, INTERACTION_TYPES.STATUS_DOT);
                });
            }
        });
    });

    const $submit = $container.find('.btn[type="submit"]');
    $categoryLinks.on('click.selectCategory', () => {
        $submit.prop('disabled', '');
        if (!isMobile) {
            $container.find('.content').removeClass('unselected-category');
        }
        $categoryLinks.off('click.selectCategory');
    });

    $categoryLinks.on('click', (e) => {
        const $link = $(e.currentTarget);
        if (!$link.hasClass(CLASS_NAME_ACTIVE)) {
            const data = $link.data();

            $categoryLinks.removeClass(CLASS_NAME_ACTIVE);
            $link.addClass(CLASS_NAME_ACTIVE);
            $link.find('input').prop('checked', true);

            $categoryItems.removeClass(CLASS_NAME_ACTIVE);
            $categoryItems.filter(data.target).addClass(CLASS_NAME_ACTIVE);

            const eventData = {
                action: 'click',
                element_id: 'clicked_nux_category_option',
                element_type: 'button',
            };

            if (enableNuxSegmentation) {
                eventData.user_category = data.axUserCategory;
                eventData.nux_school_level_category = data.axLevelCategory;
                eventData.nux_use_case_category = data.axUseCaseCategory;
            } else {
                eventData.user_category = data.category;
            }

            track('ui', eventData);
        }
    });

    // preload category images
    $categoryItems.find('.img').each((i, elem) => {
        const bgPropValue = window.getComputedStyle(elem).backgroundImage;
        if (bgPropValue) {
            const img = new Image();
            [, img.src] = bgPropValue.match(/url\(['"]?(.*?)['"]?\)/);
        }
    });

    _.defer(trackStep);

    window.onpopstate = () => {
        slide = getSlideNumberFromURL();
        switchSlide(slide, document.location, INTERACTION_TYPES.PREV_NEXT_BUTTON);
    };

    // move the position:fixed footer to BODY to avoid z-index issues on overscroll (BP-18081)
    if (isMobile) {
        const FOOTER_SELECTOR = '.nux-segmentation-form-footer';
        const $segmentationForm = $('.nux-welcome-form form');
        const $formFooter = $segmentationForm.find(FOOTER_SELECTOR);

        $body.children(FOOTER_SELECTOR).remove();
        $formFooter.appendTo($body);
        $submit.on('click', (e) => {
            e.preventDefault();
            $segmentationForm.trigger('submit');
        });
        $segmentationForm.on('submit', () => {
            $submit.prop('disabled', true);
        });
        toggleFooter = (stepNumber) => {
            $formFooter.toggleClass(CLASS_NAME_HIDE, stepNumber !== 5);
        };
        toggleFooter(slide);
    }
};


nux.earthAnimation = ($container) => {
    Promise.all([
        import(/* webpackChunkName: 'd3-nux' */ 'd3-geo'),
        import(/* webpackChunkName: 'd3-nux' */ 'd3-scale'),
        import(/* webpackChunkName: 'd3-nux' */ 'd3-selection'),
    ]).then(([
        { geoPath, geoOrthographic },
        { scaleLinear },
        { select },
    ]) => {
        const width = $container.width();
        const height = $container.height();
        const originalSize = 200;
        const mapScale = 100 * (width / originalSize);

        const projection = geoOrthographic()
            .scale(mapScale)
            .translate([width / 2, height / 2])
            .clipAngle(90);


        const mapPath = geoPath()
            .projection(projection);

        const lambda = scaleLinear()
            .domain([0, width])
            .range([-180, 180]);

        const svg = select($container[0]).append('svg')
            .attr('width', width)
            .attr('height', height);

        // eslint-disable-next-line
        const world = {"type":"Topology","transform":{"scale":[0.036003600360036005,0.017366249624962502],"translate":[-180,-90]},"objects":{"land":{"type":"GeometryCollection","geometries":[{"type":"Polygon","arcs":[[0]]},{"type":"Polygon","arcs":[[1]]},{"type":"Polygon","arcs":[[2]]},{"type":"Polygon","arcs":[[3]]},{"type":"Polygon","arcs":[[4]]},{"type":"Polygon","arcs":[[5]]},{"type":"Polygon","arcs":[[6]]},{"type":"Polygon","arcs":[[7]]},{"type":"Polygon","arcs":[[8,9]]},{"type":"Polygon","arcs":[[10,-10]]},{"type":"Polygon","arcs":[[11]]},{"type":"Polygon","arcs":[[12]]},{"type":"Polygon","arcs":[[13]]},{"type":"Polygon","arcs":[[14]]},{"type":"Polygon","arcs":[[15]]},{"type":"Polygon","arcs":[[16]]},{"type":"Polygon","arcs":[[17]]},{"type":"Polygon","arcs":[[18]]},{"type":"Polygon","arcs":[[19]]},{"type":"Polygon","arcs":[[20]]},{"type":"Polygon","arcs":[[21]]},{"type":"Polygon","arcs":[[22]]},{"type":"Polygon","arcs":[[23]]},{"type":"Polygon","arcs":[[24]]},{"type":"Polygon","arcs":[[25]]},{"type":"Polygon","arcs":[[26]]},{"type":"Polygon","arcs":[[27]]},{"type":"Polygon","arcs":[[28]]},{"type":"Polygon","arcs":[[29]]},{"type":"Polygon","arcs":[[30]]},{"type":"Polygon","arcs":[[31]]},{"type":"Polygon","arcs":[[32]]},{"type":"Polygon","arcs":[[33]]},{"type":"Polygon","arcs":[[34]]},{"type":"Polygon","arcs":[[35]]},{"type":"Polygon","arcs":[[36]]},{"type":"Polygon","arcs":[[37]]},{"type":"Polygon","arcs":[[38]]},{"type":"Polygon","arcs":[[39]]},{"type":"Polygon","arcs":[[40]]},{"type":"Polygon","arcs":[[41]]},{"type":"Polygon","arcs":[[42]]},{"type":"Polygon","arcs":[[43]]},{"type":"Polygon","arcs":[[44]]},{"type":"Polygon","arcs":[[45],[46]]},{"type":"Polygon","arcs":[[47]]},{"type":"Polygon","arcs":[[48]]},{"type":"Polygon","arcs":[[49]]},{"type":"Polygon","arcs":[[50]]},{"type":"Polygon","arcs":[[51]]},{"type":"Polygon","arcs":[[52]]},{"type":"Polygon","arcs":[[53]]}],"bbox":[-180,-90.00000000000003,180.00000000000014,83.64513000000002]}},"arcs":[[[3345,574],[-16,-56],[-171,43],[187,13]],[[3745,688],[51,-114],[-264,-54],[116,168],[97,0]],[[3098,1097],[-9,-70],[-150,-12],[109,201],[50,-119]],[[0,304],[279,48],[330,-86],[265,-13],[157,60],[-297,50],[20,95],[-111,54],[172,-13],[108,81],[-238,50],[-45,101],[112,-11],[189,59],[50,54],[253,71],[431,-10],[160,44],[177,-85],[192,-7],[-84,155],[204,-58],[220,35],[338,-55],[203,55],[47,79],[-35,88],[21,161],[132,154],[24,-89],[-94,-80],[111,-202],[-13,-193],[-239,-126],[-185,-5],[82,-48],[-101,-48],[71,-108],[436,-122],[40,-48],[235,85],[194,-20],[396,100],[-32,62],[-165,-11],[-4,64],[191,96],[213,44],[162,104],[-2,55],[144,109],[279,-22],[271,94],[270,7],[92,-46],[262,59],[52,67],[133,-73],[365,203],[127,15],[167,-117],[181,5],[18,-101],[-44,-125],[85,-14],[79,128],[105,24],[143,129],[144,58],[74,-59],[252,-1],[87,97],[93,-79],[206,61],[202,-76],[40,37],[180,-12],[165,32],[75,-43],[224,2],[32,-56],[212,-38],[233,-126],[237,-55],[-54,-113],[-140,-104],[-21,-92],[98,-97],[-145,-24],[-55,-103],[75,-64],[298,-135],[140,-4],[-9951,-14]],[[3118,2082],[75,-49],[-86,-53],[-80,32],[66,140],[25,-70]],[[9038,2834],[80,-5],[-10,-135],[-52,-19],[-18,159]],[[9805,2826],[34,-49],[-136,-280],[-64,88],[166,241]],[[9849,3100],[109,-88],[-70,-207],[-61,102],[22,193]],[[0,4229],[9981,-14],[2,24],[-9983,18]],[[0,4257],[0,-28]],[[0,4257],[0,-28]],[[6390,4402],[11,-96],[-93,-560],[-85,-2],[-20,127],[31,378],[91,93],[41,147],[24,-87]],[[8987,4390],[28,-24],[50,-275],[180,-364],[7,-325],[-102,-395],[-87,-72],[-157,58],[-30,109],[-99,71],[-48,131],[-82,65],[-144,-42],[-69,-96],[-156,-68],[-84,50],[22,115],[-67,450],[21,152],[186,119],[60,189],[113,149],[36,-61],[111,216],[127,-71],[-42,-136],[106,-153],[45,0],[46,386],[29,-178]],[[8017,4792],[59,18],[106,-131],[-256,109],[91,4]],[[8725,5116],[37,-127],[80,95],[173,-124],[116,-302],[-23,-59],[-88,144],[-59,-98],[-110,116],[-21,111],[-118,107],[-87,149],[100,-12]],[[8478,5264],[-22,-57],[-118,-11],[83,-283],[-80,101],[3,-150],[-46,157],[59,237],[121,6]],[[7939,4845],[-90,94],[-111,348],[-89,182],[58,16],[177,-297],[63,-182],[-8,-161]],[[8274,5288],[-48,-337],[-165,63],[-31,142],[15,142],[93,63],[104,220],[68,-87],[-36,-206]],[[8510,5667],[-28,-163],[-49,130],[77,33]],[[8369,6248],[33,-81],[-21,-159],[-47,37],[35,203]],[[2984,6327],[118,-73],[-94,-32],[-24,105]],[[2786,6493],[153,-142],[-99,-25],[-54,167]],[[5431,7384],[-12,-93],[-74,57],[86,36]],[[8915,7321],[-20,-115],[-257,-72],[9,-141],[-53,107],[89,123],[85,5],[104,155],[24,172],[44,-116],[-25,-118]],[[8997,7726],[45,-52],[-66,-73],[-50,80],[17,124],[54,-79]],[[3440,8101],[74,-83],[-21,-86],[-140,-8],[87,177]],[[8989,8105],[-3,-226],[-40,-50],[14,449],[29,-173]],[[4811,8192],[-89,-26],[8,119],[112,39],[-31,-132]],[[4916,8559],[26,-157],[71,-172],[2,-124],[-176,-35],[41,60],[19,196],[-70,125],[87,107]],[[2634,8963],[57,-89],[-67,-61],[10,150]],[[4597,9009],[24,-76],[-140,-94],[-157,122],[273,48]],[[0,9154],[281,-172],[-111,-98],[-167,92],[-3,178]],[[0,9301],[9999,-40],[-35,16],[-9964,24]],[[2485,9184],[88,-132],[51,154],[118,-41],[-3,-118],[-122,-32],[-43,-103],[-162,-158],[-42,-177],[66,-107],[278,-112],[66,-227],[36,78],[-34,121],[92,108],[-55,131],[11,202],[119,7],[118,-79],[54,-165],[85,123],[77,-230],[124,-99],[46,-143],[-121,-109],[-177,-1],[-130,-196],[126,133],[58,-167],[129,-18],[-154,-137],[-44,73],[-88,-65],[4,-118],[-81,-24],[-79,-189],[0,-137],[-156,-237],[36,-263],[-31,-96],[-81,281],[-153,5],[5,-58],[-147,18],[-74,-121],[-14,-284],[96,-248],[101,66],[14,99],[90,31],[-53,-326],[161,-51],[-20,-209],[68,-149],[51,48],[76,-56],[38,114],[104,105],[9,-85],[265,-14],[-14,-45],[145,-229],[119,-32],[81,-251],[-21,-48],[298,-178],[132,-149],[3,-204],[-106,-276],[-23,-334],[-62,-194],[-74,-22],[-106,-146],[-11,-161],[-168,-361],[-99,30],[47,-142],[-68,-105],[-86,-6],[5,-107],[-82,-22],[46,-86],[-115,-216],[54,-54],[-97,-201],[28,-93],[-80,-85],[-109,90],[-20,323],[36,147],[33,402],[48,271],[38,635],[-8,175],[-157,213],[-150,571],[37,317],[82,177],[-55,296],[-25,-83],[-161,149],[-51,160],[-99,58],[-97,131],[-51,-31],[-193,152],[-62,123],[-8,135],[-173,356],[-25,127],[-43,-58],[147,-391],[-77,79],[-80,172],[-62,306],[-93,90],[-105,329],[14,300],[-18,164],[48,36],[-128,105],[-185,420],[-362,159],[-171,-116],[-175,-190],[-149,-57],[201,173],[-170,128],[-64,98],[149,189],[-116,-19],[-88,70],[101,52],[78,-26],[-126,159],[267,142],[120,-43],[241,-26],[197,-72],[232,91],[67,-58],[85,22],[244,-84],[11,-41],[204,64],[51,-45],[268,3],[-50,116],[35,106],[130,-140]],[[1829,9393],[86,-39],[157,13],[26,-97],[97,-55],[-40,-74],[-99,25],[-204,-37],[-169,174],[146,90]],[[2595,9395],[118,34],[44,-97],[81,40],[251,-128],[-1,-104],[194,-107],[-58,-108],[-78,80],[48,-214],[-105,62],[-3,-81],[-165,135],[61,150],[-176,166],[-65,-25],[-205,39],[-42,105],[101,53]],[[2212,9435],[83,-5],[-27,-143],[-115,71],[59,77]],[[2411,9373],[-36,78],[111,-16],[-75,-62]],[[1654,9294],[-152,27],[28,139],[204,-6],[57,-41],[-137,-119]],[[9029,9534],[-170,-55],[-40,77],[210,-22]],[[2264,9600],[9,-99],[-122,77],[113,22]],[[1994,9570],[53,-69],[-164,-33],[11,43],[-164,3],[64,72],[176,-58],[24,42]],[[6598,9255],[-165,43],[112,208],[154,67],[194,40],[0,-41],[-183,-56],[-171,-166],[59,-95]],[[2370,9622],[152,-86],[224,6],[-23,-73],[-291,23],[-62,130]],[[1772,9654],[-4,-45],[-181,-44],[185,89]],[[0,9154],[0,-230],[9963,-25],[19,-90],[-159,-76],[-93,-102],[-39,39],[-149,-40],[-40,-289],[-148,-221],[-37,251],[81,154],[147,178],[23,81],[-121,-115],[-23,71],[-141,-116],[-334,-42],[-196,-248],[172,-94],[-35,-268],[-144,-291],[-72,-6],[-132,-203],[53,-171],[-10,-98],[-72,-40],[-9,193],[-53,126],[-89,-60],[16,118],[-114,-127],[99,-120],[-54,-100],[77,-186],[5,-107],[-96,-304],[-77,-102],[-141,-79],[-63,18],[-80,-153],[90,-218],[9,-208],[-113,-176],[-2,76],[-138,200],[-27,-198],[118,-294],[4,-209],[-59,88],[-52,264],[-38,58],[17,178],[-45,316],[-82,-51],[3,125],[-80,262],[-124,-73],[-184,-322],[-13,-319],[-65,-138],[-111,462],[-25,309],[-60,-27],[-114,262],[-135,-20],[-189,80],[-89,80],[-39,131],[-59,-9],[106,-343],[62,5],[57,112],[104,-216],[-56,-187],[-253,-291],[-145,-79],[-23,238],[-97,261],[-2,74],[-110,315],[-66,37],[132,-581],[49,-155],[113,-203],[22,-112],[194,91],[-15,-163],[-111,-365],[-175,-313],[-41,-224],[47,-247],[8,-227],[-37,-116],[-129,-177],[7,-270],[-57,-51],[-22,-195],[-111,-232],[-67,-67],[-172,-51],[-122,445],[-26,287],[-73,313],[55,267],[-48,403],[-86,226],[28,241],[-36,98],[-73,-29],[-43,116],[-175,-90],[-154,-22],[-137,169],[-144,430],[32,81],[-14,331],[70,251],[136,212],[25,191],[76,145],[104,-34],[101,82],[224,43],[44,-233],[115,-60],[107,-115],[49,141],[224,-106],[148,20],[53,265],[-237,48],[-41,162],[86,101],[164,47],[89,-63],[86,98],[-133,150],[68,116],[-154,-81],[-78,42],[-86,-231],[32,-87],[-167,-34],[34,-130],[-66,-79],[-59,281],[-129,193],[-64,-57],[119,-210],[-40,-22],[-182,248],[-65,-71],[-95,-3],[-146,-369],[-90,-42],[-98,53],[-13,355],[39,42],[169,-19],[19,149],[-94,154],[83,-2],[151,171],[24,85],[114,53],[-7,178],[66,-179],[89,-14],[198,83],[58,230],[129,16],[2,59],[-144,-37],[-51,159],[121,144],[-89,35],[-121,-171],[26,-154],[-80,-229],[-82,-43],[-72,237],[-130,-51],[-19,195],[154,145],[117,191],[123,116],[250,79],[51,-58],[286,-130],[-53,-111],[-124,44],[28,-135],[266,134],[-30,105],[78,-18],[3,-92],[205,126],[141,2],[48,56],[240,-71],[-69,139],[90,115],[104,-266],[-67,-121],[105,83],[-54,213],[182,50],[24,76],[175,17],[10,68],[168,53],[209,23],[34,49],[253,-33],[78,-80],[-125,-66],[383,-70],[2,44],[148,-40],[75,-129],[27,60],[211,-20],[17,78],[251,-37],[96,-78],[168,1],[54,-82],[264,38],[226,-40],[-9960,-25]],[[6364,7560],[2,-213],[129,-36],[-26,225],[51,5],[-123,210],[76,38],[-52,103],[-125,-141],[68,-191]],[[7918,9692],[-157,-23],[51,76],[106,-53]],[[5506,9772],[-31,-167],[-164,119],[54,66],[141,-18]],[[5706,9813],[14,-52],[-163,3],[-75,43],[224,6]],[[7775,9725],[-184,31],[-59,53],[132,52],[118,-85],[-7,-51]],[[2583,9770],[-56,-80],[-107,4],[-107,104],[120,64],[150,-92]],[[3097,9968],[183,-43],[-258,-147],[-159,-28],[42,-46],[-143,-135],[-248,17],[122,165],[-180,147],[341,71],[300,-1]],[[4247,9992],[174,-46],[-308,-30],[243,-61],[206,44],[98,-36],[-153,-66],[-55,-80],[34,-101],[-25,-155],[-116,-57],[34,-183],[-150,-96],[-112,-20],[-67,-83],[-156,-70],[-84,-160],[-15,-149],[-136,44],[-94,159],[-64,205],[86,158],[-72,-37],[-66,137],[31,53],[-108,169],[-74,34],[-201,-3],[-129,137],[206,55],[-64,42],[215,110],[376,34],[324,59],[222,-7]]]};

        svg.append('path')
            .datum(topojson.feature(world, world.objects.land))
            .attr('class', 'land')
            .attr('d', mapPath);

        const scrollSpeed = 20;
        let current = 0;
        setInterval(() => {
            current += 0.5;
            projection.rotate([lambda(current), 0]);
            svg.selectAll('path').attr('d', mapPath);
        }, scrollSpeed);
    });
};


module.exports = nux;
